import { BulkActionsBarOutlet, BulkActionsBarProvider } from '@calendly/ui/components/bulk-actions-bar';
import { ContentArea } from '@calendly/ui/components/content-area';
import { PageHeaderContext, PageHeaderOutlet } from '@calendly/ui/components/page-header';
import { MainContainerSizeProvider } from '@calendly/ui/hooks/main-container-size-context';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { forwardRef, useCallback, useMemo } from 'react';
import { useGetUserQuery } from 'common';
import { JoinAnOrgBanner } from 'components/home_bar';
import { BrandRefreshBetaGlobalHeader, BrandRefreshWelcomeGate } from 'features/app_shell';
import { PageFilterContext, usePageFilterContext } from 'features/app_shell/shell_layout/pageFilterContext';
import { Header, PretendModeHeader } from 'features/header';
import { MainContainer, MainRegion, useIsHeaderBorderBottom } from 'features/layout/main_region';
import { StickyFooterContext, StickyFooterOutlet } from 'features/layout/sticky_footer/sticky_footer';
import { LeftNavigation, LeftNavigationContext } from 'features/left_navigation';
import { MfeShareableOutlet } from 'features/mfe_shareable';
import { OrgConsolidationGlobalNotification } from 'features/organization_consolidation';
import { RightRailOutlet } from 'features/right_rail';
import { SeatRequestsBanner } from 'features/seat_requests';
import { SidebarPlaceholder } from 'features/sidebar_placeholder';
import { TermsOfService } from 'features/terms_of_service';
import { useStickyOffset } from './use_sticky_offset';
const contentContainerCss = "c5lnar5";
const contentScrollCss = "c15j6k6x";
const legacyFilterOutletCss = "lkef718";

/* Portal target for PageFilterRenderer in the legacy shell.
   - Reset the bleed wrapper's extra bottom padding/margin (no gradient overlay here).
   - Override the content div's width so filters stay fluid and don't
     cause horizontal scroll */
const legacyFilterPortalTargetCss = "l1jkdrzg";
const fullPageHostCss = "fgr40oh";
const ContentWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    children,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(contentContainerCss, props.className)}>
      <BrandRefreshBetaGlobalHeader />

      <div className={contentScrollCss}>
        {children}
      </div>
    </Component>;
});

/**
 * Sticky filter outlet for the legacy shell. Sits between PageHeaderOutlet and
 * MainRegion in the scroll container, stacking below the page header.
 *
 * Renders nothing when no `PageFilterRenderer` is mounted, so pages without
 * filters get zero layout impact.
 */
const LegacyPageFilterOutlet = () => {
  const {
    setFilterRef,
    isRendered
  } = usePageFilterContext();
  const stickyTop = useStickyOffset();
  const outerRef = useCallback(node => {
    if (node) node.style.top = `${stickyTop}px`;
  }, [stickyTop]);
  if (!isRendered) return null;
  return <div className={legacyFilterOutletCss} ref={outerRef} data-testid="legacy-page-filter-outlet">
      <ContentArea padding="inline">
        <div ref={setFilterRef} className={legacyFilterPortalTargetCss} />
      </ContentArea>
    </div>;
};

/**
 * Reads the headerBorderBottom layout signal set by pages that have no page header.
 * Without a page header the global header has no visual separator below it, so we
 * render a bottom border here as a substitute. See `useHeaderBorderBottom`.
 */
const LayoutHeader = ({
  isPretending
}) => {
  const [showBorderBottom] = useIsHeaderBorderBottom();
  return <Header isPretending={isPretending} showBorderBottom={showBorderBottom} />;
};

/** Pre–product-refresh shell: same flex structure as `SideNavLayout` on `main`. */
export const LegacyAppLayout = ({
  children,
  noSidebar,
  fullPage
}) => {
  const {
    data: {
      is_pretending: isPretending
    }
  } = useGetUserQuery();
  const isPhone = useDeviceType('isPhone');
  const pageHeaderOffsetTop = useMemo(() => {
    let offset = 0;
    if (isPretending) offset += 48;
    if (isPhone) offset += 72;
    return offset;
  }, [isPretending, isPhone]);
  if (noSidebar) {
    return children;
  }
  if (fullPage) {
    return <div className={fullPageHostCss} data-testid="full-page-host">
        <PretendModeHeader />
        <TermsOfService />
        {children}
      </div>;
  }
  return <PageHeaderContext offsetTop={pageHeaderOffsetTop}>
      <PageFilterContext>
        <BulkActionsBarProvider>
          <StickyFooterContext>
            <LeftNavigationContext>
              <MainContainer>
                <LeftNavigation />
                <MainContainerSizeProvider>
                  {sizeRef => <>
                      <ContentWrapper ref={sizeRef} data-component="main">
                        <LayoutHeader isPretending={isPretending} />
                        <SeatRequestsBanner />
                        <PageHeaderOutlet />
                        <LegacyPageFilterOutlet />
                        <MainRegion>
                          <OrgConsolidationGlobalNotification />
                          <JoinAnOrgBanner />
                          {children}
                        </MainRegion>
                        <StickyFooterOutlet />
                        <BulkActionsBarOutlet />
                      </ContentWrapper>
                      <MfeShareableOutlet />
                      <RightRailOutlet />
                      <SidebarPlaceholder />
                    </>}
                </MainContainerSizeProvider>
              </MainContainer>
              <BrandRefreshWelcomeGate />
            </LeftNavigationContext>
          </StickyFooterContext>
        </BulkActionsBarProvider>
      </PageFilterContext>
    </PageHeaderContext>;
};

require("./legacy_app_layout.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./legacy_app_layout.js");